import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { X, Search, FolderOpen, Check } from "lucide-react";
import { Input } from "@/components/ui/input";

const STORAGE_BASE = "https://bhvsqiffenuabtwekgsj.supabase.co/storage/v1/object/public/images";

interface StorageFile {
  path: string;
  url: string;
  name: string;
  folder: string;
}

interface StorageImagePickerProps {
  onSelect: (paths: string[]) => void;
  onClose: () => void;
}

async function listFolder(folder: string): Promise<StorageFile[]> {
  const { data } = await supabase.storage.from("images").list(folder, { limit: 200, sortBy: { column: "name", order: "asc" } });
  if (!data) return [];

  const files: StorageFile[] = [];
  const subdirPromises: Promise<StorageFile[]>[] = [];

  for (const item of data) {
    if (item.id === null) {
      const sub = folder ? `${folder}/${item.name}` : item.name;
      subdirPromises.push(listFolder(sub));
    } else {
      const path = folder ? `${folder}/${item.name}` : item.name;
      if (/\.(webp|jpg|jpeg|png|gif|svg|avif)$/i.test(item.name)) {
        files.push({ path, url: `${STORAGE_BASE}/${path}`, name: item.name, folder: folder || "(racine)" });
      }
    }
  }

  const subResults = await Promise.all(subdirPromises);
  return [...files, ...subResults.flat()];
}

export default function StorageImagePicker({ onSelect, onClose }: StorageImagePickerProps) {
  const [allFiles, setAllFiles] = useState<StorageFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [activeFolder, setActiveFolder] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  useEffect(() => {
    listFolder("").then((files) => {
      setAllFiles(files);
      setLoading(false);
    });
  }, []);

  const folders = Array.from(new Set(allFiles.map((f) => f.folder))).sort();

  const filtered = allFiles.filter((f) => {
    const matchFolder = activeFolder === null || f.folder === activeFolder;
    const matchSearch = f.path.toLowerCase().includes(search.toLowerCase());
    return matchFolder && matchSearch;
  });

  const toggleSelect = (path: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path);
      else next.add(path);
      return next;
    });
  };

  const handleConfirm = () => {
    if (selected.size > 0) {
      onSelect(Array.from(selected));
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl flex flex-col overflow-hidden"
        style={{ height: "90vh" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b shrink-0">
          <h2 className="text-lg font-semibold text-gray-800">Bibliothèque d'images</h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100"><X size={20} /></button>
        </div>

        {/* Toolbar */}
        <div className="flex gap-3 px-6 py-3 border-b bg-gray-50 shrink-0">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              className="pl-8 text-sm h-9"
              placeholder="Rechercher une image…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          {selected.size > 0 && (
            <button
              onClick={handleConfirm}
              className="flex items-center gap-2 px-4 py-1.5 rounded-lg text-sm font-medium text-white transition-opacity"
              style={{ backgroundColor: "#4a7c59" }}
            >
              <Check size={14} />
              Ajouter {selected.size} image{selected.size > 1 ? "s" : ""}
            </button>
          )}
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Sidebar dossiers */}
          <aside className="w-52 shrink-0 border-r overflow-y-auto py-2">
            <button
              onClick={() => setActiveFolder(null)}
              className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 ${activeFolder === null ? "bg-green-50 text-green-700 font-medium" : "hover:bg-gray-50 text-gray-600"}`}
            >
              <FolderOpen size={14} /> Tous ({allFiles.length})
            </button>
            {folders.map((f) => {
              const count = allFiles.filter((img) => img.folder === f).length;
              return (
                <button
                  key={f}
                  onClick={() => setActiveFolder(f)}
                  className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 ${activeFolder === f ? "bg-green-50 text-green-700 font-medium" : "hover:bg-gray-50 text-gray-600"}`}
                >
                  <FolderOpen size={14} className="shrink-0" />
                  <span className="truncate flex-1">{f}</span>
                  <span className="text-xs text-gray-400 shrink-0">{count}</span>
                </button>
              );
            })}
          </aside>

          {/* Grille images */}
          <div className="flex-1 overflow-y-auto p-5">
            {loading ? (
              <p className="text-sm text-gray-400 text-center mt-10">Chargement…</p>
            ) : filtered.length === 0 ? (
              <p className="text-sm text-gray-400 text-center mt-10">Aucune image trouvée.</p>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3">
                {filtered.map((file) => {
                  const isSelected = selected.has(file.path);
                  return (
                    <button
                      key={file.path}
                      onClick={() => toggleSelect(file.path)}
                      className={`group relative aspect-square rounded-lg overflow-hidden border-2 transition-all focus:outline-none ${
                        isSelected
                          ? "border-green-500 ring-2 ring-green-300"
                          : "border-transparent hover:border-green-400"
                      }`}
                      title={file.path}
                    >
                      <img
                        src={file.url}
                        alt={file.name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                      <div className={`absolute inset-0 transition-colors ${isSelected ? "bg-green-500/20" : "bg-black/0 group-hover:bg-black/10"}`} />
                      {isSelected && (
                        <div className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-green-500 flex items-center justify-center shadow">
                          <Check size={11} className="text-white" strokeWidth={3} />
                        </div>
                      )}
                      <p className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[10px] px-1.5 py-1 truncate opacity-0 group-hover:opacity-100 transition-opacity">
                        {file.name}
                      </p>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t bg-gray-50 flex items-center justify-between shrink-0">
          <span className="text-xs text-gray-400">
            {filtered.length} image{filtered.length !== 1 ? "s" : ""}
            {selected.size > 0 && ` · ${selected.size} sélectionnée${selected.size > 1 ? "s" : ""}`}
          </span>
          <div className="flex gap-2">
            {selected.size > 0 && (
              <button
                onClick={() => setSelected(new Set())}
                className="text-xs text-gray-500 hover:text-gray-700 px-3 py-1.5 rounded border border-gray-200 hover:bg-gray-100"
              >
                Tout désélectionner
              </button>
            )}
            <button
              onClick={handleConfirm}
              disabled={selected.size === 0}
              className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-medium text-white disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ backgroundColor: "#4a7c59" }}
            >
              <Check size={14} />
              {selected.size === 0 ? "Sélectionner des images" : `Ajouter ${selected.size} image${selected.size > 1 ? "s" : ""}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
